import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Clock, X, Info, Trophy, ShieldCheck, Send, Sparkles, Loader2, UserPlus, MessageSquareText, Flame, History } from "lucide-react";
import { toast } from "sonner";
import { compromisoApi, apiError, isParentView } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ParentViewBar } from "../components/ParentViewBar";

const DAYS = ["L", "M", "M", "J", "V", "S", "D"];

export default function Compromisos() {
  const navigate = useNavigate();
  const readOnly = isParentView();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try { setData(await compromisoApi.me()); } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);

  const toggle = async (act, slot, mark) => {
    if (busy || readOnly) return;
    setBusy(true);
    try {
      if (!mark) { await compromisoApi.mark(act.id, slot); toast.success("Enviado a tu papá/mamá para validar."); }
      else { await compromisoApi.unmark(mark.id); }
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const linkParent = async () => {
    if (!email.trim()) return;
    try { const r = await compromisoApi.linkParent(email.trim()); toast.success(`Vinculado: ${r.parent.nombre}`); setShowLink(false); setEmail(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    setSubmitting(true);
    try { await compromisoApi.submit(); toast.success("Entrega enviada a tu papá/mamá para validación final."); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  const openHistory = async () => {
    try { setHistory(await compromisoApi.history()); setShowHistory(true); } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="min-h-screen bg-cream grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[#E9A400]" /></div>;

  if (!data.enabled) {
    return (
      <div className="min-h-screen bg-cream grid place-items-center px-6 text-center">
        <div><Info className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 font-bold text-[#0A1C30]">La Hoja de Compromisos no está disponible para tu programa.</p>
          <button onClick={() => navigate("/mi-cuenta")} className="mt-4 rounded-xl bg-navy text-white font-bold px-5 py-2.5">Volver</button></div>
      </div>
    );
  }

  const complete = data.validadas >= data.total;
  const inStaffReview = data.sheet_status === "en_revision_staff";
  const canSubmit = complete && data.is_due_day && data.sheet_status !== "entregada"
    && data.full_validation?.status !== "pendiente" && !inStaffReview;

  return (
    <div className="min-h-screen bg-cream pb-24" data-testid="compromisos-page">
      <header className="bg-navy">
        <div className="max-w-3xl mx-auto px-6 h-20 flex items-center justify-between">
          <button data-testid="comp-back" onClick={() => navigate("/mi-cuenta")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold"><ArrowLeft className="w-4 h-4" /> Mi cuenta</button>
          <img src="/logo-supera-light.png" alt="Supera" className="h-11 w-auto object-contain" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <ParentViewBar />
        {/* Hero + progress */}
        <div className="rounded-3xl bg-gradient-to-br from-[#0A1C30] to-[#16324f] p-6 md:p-8 text-white relative overflow-hidden">
          <Sparkles className="absolute -right-4 -top-4 w-28 h-28 text-white/5" />
          <p className="text-xs font-extrabold tracking-[0.25em] text-[var(--gold)]">METODOLOGÍA SUPERA</p>
          <h1 className="text-2xl md:text-3xl font-extrabold mt-1">Hoja de Compromisos</h1>
          <p className="text-sm text-white/70 mt-2">Cada acción concreta te forja como líder. ¡Marca lo que cumpliste esta semana!</p>
          <div className="mt-5">
            <div className="flex justify-between text-sm font-bold mb-1.5"><span>Avance semanal</span><span data-testid="comp-progress">{data.progress}%</span></div>
            <div className="h-3 rounded-full bg-white/15 overflow-hidden"><div className="h-full rounded-full bg-[var(--gold)] transition-all duration-500" style={{ width: `${data.progress}%` }} /></div>
            <p className="text-xs text-white/60 mt-1.5">{data.validadas} de {data.total} actividades validadas</p>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs"><Info className="w-4 h-4 text-[var(--gold)] flex-shrink-0" /> {data.aviso_reinicio}</div>
          <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="streak-block">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gold)] text-[#0A1C30] font-extrabold px-3 py-1.5 text-sm">
              <Flame className="w-4 h-4" /> Racha: {data.streak} {data.streak === 1 ? "semana" : "semanas"}
            </span>
            {(data.badges || []).map((b) => (
              <span key={b.nombre} title={`${b.semanas} semanas`} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-sm font-bold">{b.emoji} {b.nombre}</span>
            ))}
            {data.next_badge && (
              <span className="text-xs text-white/60">Próxima insignia: {data.next_badge.emoji} {data.next_badge.nombre} ({data.next_badge.semanas} semanas)</span>
            )}
            <button data-testid="history-btn" onClick={openHistory} className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-white/70 hover:text-white"><History className="w-4 h-4" /> Historial</button>
          </div>
        </div>

        {/* Parent link warning */}
        {!data.has_parent && !readOnly && (
          <div className="mt-4 rounded-2xl bg-[#FBF0CE] ring-1 ring-[#E9A400]/30 p-4 flex items-center gap-3" data-testid="no-parent-warning">
            <ShieldCheck className="w-6 h-6 text-[#B9860B] flex-shrink-0" />
            <div className="flex-1"><p className="font-bold text-[#8A6D0B] text-sm">Vincula a tu papá o mamá</p><p className="text-xs text-[#8A6D0B]">Cada actividad debe ser validada por tu padre/madre para contar.</p></div>
            <button data-testid="link-parent-btn" onClick={() => setShowLink(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-navy text-white font-bold px-3 py-2 text-sm"><UserPlus className="w-4 h-4" /> Vincular</button>
          </div>
        )}

        {/* Completed banner */}
        {complete && data.sheet_status !== "entregada" && (
          <div className="mt-4 rounded-2xl bg-[#E2F6EB] ring-1 ring-[#22A860]/30 p-4 flex items-center gap-3">
            <Trophy className="w-6 h-6 text-[#22A860] flex-shrink-0" />
            <p className="flex-1 font-bold text-[#177A45] text-sm">¡Cumpliste el 100%! {data.is_due_day ? "Ya puedes entregar tu hoja." : "Podrás entregarla el domingo (no se aceptan entregas anticipadas)."}</p>
          </div>
        )}
        {data.sheet_status === "entregada" && (
          <div className="mt-4 rounded-2xl bg-[#E2F6EB] ring-1 ring-[#22A860]/30 p-4 flex items-center gap-3"><Check className="w-6 h-6 text-[#22A860]" /><p className="font-bold text-[#177A45] text-sm">Hoja entregada y validada por el equipo Supera. ¡Sumaste tus puntos de graduación!</p></div>
        )}
        {inStaffReview && (
          <div className="mt-4 rounded-2xl bg-[#EAF1FB] ring-1 ring-[#2F6FD0]/30 p-4 flex items-center gap-3" data-testid="comp-en-revision">
            <Clock className="w-6 h-6 text-[#2F6FD0] flex-shrink-0" />
            <p className="flex-1 font-bold text-[#1E4E8C] text-sm">Tu hoja fue validada por tu familia y está en revisión del equipo Supera. Los puntos de graduación se sumarán cuando la aprueben.</p>
          </div>
        )}

        {/* Aviso permanente: cómo se suman los puntos */}
        <div className="mt-4 rounded-2xl bg-[#FFFBEB] ring-1 ring-[var(--gold)]/30 p-4 flex items-start gap-3" data-testid="comp-aviso-puntos">
          <Info className="w-5 h-5 text-[#B9860B] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#8A6D0B] leading-snug">Los puntos de graduación Supera se suman <b>solo</b> cuando: 1) cumples el <b>100%</b> de las actividades de la semana, 2) tu papá o mamá <b>validan</b> cada una y la hoja completa, y 3) el <b>equipo Supera</b> aprueba la hoja en revisión. Si la semana no queda completa, tus actividades validadas se guardan pero la hoja no se envía a revisión.</p>
        </div>


        {/* Sections */}
        <div className="mt-6 space-y-5">
          {data.secciones.map((sec) => (
            <div key={sec.id} className="bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden" data-testid={`section-${sec.nombre}`}>
              <div className="px-5 py-3 bg-[#FAF8F1] border-b border-black/5"><h2 className="font-extrabold text-[#0A1C30]">{sec.nombre}</h2></div>
              <div className="divide-y divide-black/5">
                {sec.actividades.map((act) => (
                  <div key={act.id} className="px-5 py-3.5 flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[#2A3446]">{act.nombre}</p>
                      <p className="text-[11px] text-[#9AA3B2] mt-0.5">{act.frecuencia_tipo === "daily" ? "Diario" : act.frecuencia_tipo === "weekly" ? "1 vez/semana" : `${act.required} veces/semana`}</p>
                    </div>
                    <div className="flex gap-1 flex-wrap justify-end flex-shrink-0">
                      {Array.from({ length: act.required }).map((_, i) => {
                        const mark = act.marks[String(i)];
                        const st = mark?.status;
                        const label = act.frecuencia_tipo === "daily" ? DAYS[i] : (act.required > 1 ? i + 1 : "✓");
                        const cls = st === "validada" ? "bg-[#22A860] text-white border-[#22A860]"
                          : st === "pendiente" ? "bg-[#FBF0CE] text-[#B9860B] border-[#E9A400]"
                          : st === "rechazada" ? "bg-[#FDECEC] text-[#C0392B] border-[#E8A0A0]"
                          : "bg-white text-[#9AA3B2] border-[#DDE2E9] hover:border-[#E9A400]";
                        return (
                          <button key={i} data-testid={`slot-${act.id}-${i}`} onClick={() => toggle(act, i, mark)} disabled={busy || readOnly}
                            className={`w-8 h-8 rounded-lg border text-xs font-bold flex items-center justify-center transition-colors ${cls} ${readOnly ? "cursor-default" : ""}`}>
                            {st === "validada" ? <Check className="w-4 h-4" /> : st === "pendiente" ? <Clock className="w-3.5 h-3.5" /> : st === "rechazada" ? <X className="w-3.5 h-3.5" /> : label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-xs text-[#6B7688] flex items-center gap-1.5"><MessageSquareText className="w-4 h-4" /> ¿Dudas o crees que hay una falla? Contacta al staff por el <button onClick={() => navigate("/chat/staff")} className="underline font-bold text-[#0A1C30]">Chat Privado Supera</button>.</p>
      </div>

      {/* Sticky submit */}
      {canSubmit && !readOnly && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-black/10 p-4 z-30">
          <div className="max-w-3xl mx-auto">
            <button data-testid="comp-submit" onClick={submit} disabled={submitting} className="w-full rounded-2xl bg-[var(--gold)] text-[#0A1C30] font-extrabold py-3.5 inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Entregar hoja y sumar puntos Supera
            </button>
          </div>
        </div>
      )}
      {data.full_validation?.status === "pendiente" && (
        <div className="fixed bottom-0 inset-x-0 bg-[#FBF0CE] border-t border-[#E9A400]/30 p-4 text-center text-sm font-bold text-[#8A6D0B] z-30">Entrega enviada. Esperando validación final de tu papá/mamá...</div>
      )}

      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent data-testid="history-dialog">
          <DialogHeader><DialogTitle>Historial de hojas</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-black/5">
            {history.length === 0 && <p className="text-sm text-[#6B7688] py-6 text-center">Aún no tienes hojas anteriores.</p>}
            {history.map((h) => (
              <div key={h.week_start} className="flex items-center justify-between py-3">
                <div><p className="font-bold text-[#0A1C30] text-sm">Semana del {h.week_start}</p><p className="text-xs text-[#6B7688]">Entrega: {h.due_date || "—"}</p></div>
                {h.puntos_otorgados ? <span className="text-xs font-bold text-[#22A860] bg-[#E2F6EB] px-2.5 py-1 rounded-full">Entregada +{h.puntos} pts</span>
                  : <span className="text-xs font-bold text-[#6B7688] bg-[#EDF0F4] px-2.5 py-1 rounded-full">{h.status === "vencida" ? "Vencida" : "Sin entregar"}</span>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showLink} onOpenChange={setShowLink}>
        <DialogContent data-testid="link-parent-dialog">
          <DialogHeader><DialogTitle>Vincular a mi papá o mamá</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7688]">Ingresa el correo de la cuenta de Padres y Madres. Recibirá tus validaciones por notificación.</p>
          <div><Label className="mb-1 block font-bold text-xs">Correo del padre/madre</Label><Input data-testid="parent-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="papa@correo.com" /></div>
          <DialogFooter><button data-testid="link-parent-save" onClick={linkParent} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5">Vincular</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
